// ═══════════════════════════════════════════════════════════════
// ManualResearchModal — Capture public-record research per donor
// Path: src/components/donorDetail/ManualResearchModal.jsx
//
// Opens from the Capacity tab. Stores the director's findings in
// donor_wealth_profile with source="manual" so the data flows
// through the same confidence engine + wealth display as vendor
// imports — with the same 15% default contribution.
// ═══════════════════════════════════════════════════════════════

import React, { useState } from "react";
import {
  Search,
  Save,
  X,
  Plus,
  Trash2,
  ExternalLink,
  Info,
} from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { useApp } from "../../context/AppContext";
import { Modal } from "../ui/Modal";
import { ActionButton } from "../ui/ActionButton";
import { InfoBubble } from "../ui/InfoBubble";

// Public sources cheat-sheet — rendered inside the modal so the
// director has the research links at hand while entering findings.
// URLs open in a new tab; a county-assessor lookup is a web search
// since the site varies by county.
const PUBLIC_SOURCES = [
  {
    label: "Real estate",
    links: [
      {
        name: "Zillow",
        url: "https://www.zillow.com/",
        hint: "quick estimate by address",
      },
      {
        name: "County assessor lookup",
        url: "https://www.google.com/search?q=county+assessor+property+lookup",
        hint: "authoritative but varies by county",
      },
    ],
  },
  {
    label: "Public stock holdings",
    links: [
      {
        name: "SEC EDGAR full-text search",
        url: "https://efts.sec.gov/LATEST/search-index?q=&dateRange=custom&forms=4",
        hint: "insider Form 4 filings by name",
      },
      {
        name: "SEC EDGAR company search",
        url: "https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany",
        hint: "drill into a company's filings",
      },
    ],
  },
  {
    label: "Political giving",
    links: [
      {
        name: "FEC.gov individual contributions",
        url: "https://www.fec.gov/data/receipts/individual-contributions/",
        hint: "federal, authoritative",
      },
      {
        name: "OpenSecrets donor lookup",
        url: "https://www.opensecrets.org/donor-lookup",
        hint: "federal, aggregated & easier to scan",
      },
    ],
  },
  {
    label: "Foundation affiliations (990-PF)",
    links: [
      {
        name: "ProPublica Nonprofit Explorer",
        url: "https://projects.propublica.org/nonprofits/",
        hint: "free 990 and 990-PF lookups",
      },
      {
        name: "Candid (Foundation Directory)",
        url: "https://candid.org/",
        hint: "free tier requires signup",
      },
    ],
  },
  {
    label: "Business / board seats",
    links: [
      {
        name: "LinkedIn people search",
        url: "https://www.linkedin.com/search/results/people/",
        hint: "public profiles",
      },
      {
        name: "OpenCorporates",
        url: "https://opencorporates.com/",
        hint: "global company registry",
      },
    ],
  },
];

// Parse a dollar string like "$1,200,000" or "1.2M" to number
const parseDollar = (str) => {
  if (str == null || str === "") return null;
  const s = String(str).trim();
  const suffix = s.match(/^(\$?)([\d,.]+)\s*([kmb])\s*$/i);
  if (suffix) {
    const value = parseFloat(suffix[2].replace(/,/g, ""));
    if (isNaN(value)) return null;
    const mult = { k: 1000, m: 1000000, b: 1000000000 }[suffix[3].toLowerCase()];
    return value * mult;
  }
  const cleaned = s.replace(/[$,\s]/g, "");
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
};

const fmtForInput = (n) =>
  n == null
    ? ""
    : n.toLocaleString("en-US", { maximumFractionDigits: 0 });

export const ManualResearchModal = ({ member, existing, onClose }) => {
  const { palette, upsertSelectedMemberWealthProfile, showMessage } = useApp();
  const [saving, setSaving] = useState(false);

  const isVendorSourced =
    existing && existing.source && existing.source !== "manual";

  const [realEstate, setRealEstate] = useState(
    fmtForInput(existing?.realEstateValue)
  );
  const [stockValue, setStockValue] = useState(
    fmtForInput(existing?.stockValue)
  );
  const [politicalGiving, setPoliticalGiving] = useState(
    fmtForInput(existing?.politicalGivingTotal)
  );
  const [capacityRating, setCapacityRating] = useState(
    existing?.capacityRating || ""
  );
  const [foundations, setFoundations] = useState(() => {
    const list = existing?.foundationAffiliations || [];
    if (list.length === 0) return [{ name: "", role: "" }];
    return list.map((f) => ({
      name: f.name || f.raw || "",
      role: f.role || "",
    }));
  });
  const [businesses, setBusinesses] = useState(() => {
    const list = existing?.businessOwnership || [];
    if (list.length === 0) return [{ company: "", role: "" }];
    return list.map((b) => ({
      company: b.company || b.raw || "",
      role: b.role || "",
    }));
  });
  const [notes, setNotes] = useState(existing?.rawImport?.researcherNotes || "");

  const handleAddFoundation = () =>
    setFoundations((prev) => [...prev, { name: "", role: "" }]);
  const handleRemoveFoundation = (idx) =>
    setFoundations((prev) =>
      prev.length === 1
        ? [{ name: "", role: "" }]
        : prev.filter((_, i) => i !== idx)
    );
  const handleAddBusiness = () =>
    setBusinesses((prev) => [...prev, { company: "", role: "" }]);
  const handleRemoveBusiness = (idx) =>
    setBusinesses((prev) =>
      prev.length === 1
        ? [{ company: "", role: "" }]
        : prev.filter((_, i) => i !== idx)
    );

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleanedFoundations = foundations
        .map((f) => ({
          name: (f.name || "").trim(),
          role: (f.role || "").trim(),
        }))
        .filter((f) => f.name);
      const cleanedBusinesses = businesses
        .map((b) => ({
          company: (b.company || "").trim(),
          role: (b.role || "").trim(),
        }))
        .filter((b) => b.company);

      const payload = {
        capacityRating: capacityRating.trim() || null,
        realEstateValue: parseDollar(realEstate),
        stockValue: parseDollar(stockValue),
        politicalGivingTotal: parseDollar(politicalGiving),
        foundationAffiliations: cleanedFoundations,
        businessOwnership: cleanedBusinesses,
        rawImport: {
          researcherNotes: notes.trim(),
          enteredAt: new Date().toISOString(),
          ...(existing?.rawImport || {}),
          // Overwrite researcherNotes after spreading existing
          researcherNotesLatest: notes.trim(),
        },
      };

      // When overriding vendor data, preserve the source badge if it
      // was vendor-sourced (the director is refining, not replacing
      // the vendor's attribution). Pure manual rows stay "manual".
      const sourceToUse =
        isVendorSourced && existing?.source ? existing.source : "manual";

      await upsertSelectedMemberWealthProfile(payload, sourceToUse);
      showMessage("Research saved");
      onClose();
    } catch (err) {
      showMessage("Could not save research", "error");
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = {
    display: "block",
    fontSize: FONT_SIZES.xs,
    fontWeight: 700,
    color: palette.text2,
    marginBottom: 5,
    letterSpacing: 0.3,
  };

  const inputStyle = {
    width: "100%",
    padding: "8px 10px",
    borderRadius: 7,
    fontSize: FONT_SIZES.sm,
    fontFamily: FONT_FAMILY,
    background: palette.inputBg,
    color: palette.inputColor,
    border: `1px solid ${palette.inputBorder}`,
    outline: "none",
    boxSizing: "border-box",
  };

  return (
    <Modal
      title={`Research — ${member.firstName} ${member.lastName}`}
      icon={Search}
      onClose={onClose}
      width={640}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {/* Intro */}
        <div
          style={{
            padding: 12,
            borderRadius: 8,
            background: `${palette.accent1}08`,
            border: `1px dashed ${palette.cardBorder}`,
            fontSize: 12,
            color: palette.text2,
            lineHeight: 1.5,
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
          }}
        >
          <Info size={ICON_SIZES.sm} style={{ marginTop: 2, flexShrink: 0, color: palette.accent1 }} />
          <div>
            Whatever you enter here counts as the External Wealth signal
            for this donor — same 15% weight in the confidence score as a
            DonorSearch import would carry.
            {isVendorSourced && (
              <div style={{ marginTop: 6, fontWeight: 600 }}>
                Existing vendor data is pre-filled. Your edits save back
                with the vendor source preserved.
              </div>
            )}
          </div>
        </div>

        {/* Dollar fields */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
          }}
        >
          <div>
            <label style={labelStyle}>
              Real estate value
              <InfoBubble
                size={ICON_SIZES.xs}
                text="Total estimated value of primary residence plus any secondary properties. Look up on the county assessor website; Zillow for an estimate when the assessor is sparse."
              />
            </label>
            <input
              type="text"
              value={realEstate}
              onChange={(e) => setRealEstate(e.target.value)}
              placeholder="$1,200,000"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>
              Public stock value
              <InfoBubble
                size={ICON_SIZES.xs}
                text="Reportable insider holdings from SEC Form 4/13D filings. Use SEC EDGAR to search by name. Leave blank if not an insider."
              />
            </label>
            <input
              type="text"
              value={stockValue}
              onChange={(e) => setStockValue(e.target.value)}
              placeholder="$450,000"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>
              Political giving (trailing 24mo)
              <InfoBubble
                size={ICON_SIZES.xs}
                text="Total FEC-recorded federal political contributions over the past two years. Strong capacity + willingness-to-give indicator."
              />
            </label>
            <input
              type="text"
              value={politicalGiving}
              onChange={(e) => setPoliticalGiving(e.target.value)}
              placeholder="$25,000"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>
              Vendor rating (optional)
              <InfoBubble
                size={ICON_SIZES.xs}
                text="Raw rating string if you have one handy from a prior report or screen. Freeform — e.g. '$100K-$250K' or 'P2G 72'."
              />
            </label>
            <input
              type="text"
              value={capacityRating}
              onChange={(e) => setCapacityRating(e.target.value)}
              placeholder="e.g. $100K–$250K"
              style={inputStyle}
            />
          </div>
        </div>

        {/* Foundation affiliations */}
        <div>
          <label style={labelStyle}>
            Foundation affiliations
            <InfoBubble
              size={ICON_SIZES.xs}
              text="Officer or trustee roles at private foundations. Look up 990-PF filings on ProPublica Nonprofit Explorer. Add one row per foundation."
            />
          </label>
          {foundations.map((f, i) => (
            <div
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 1fr auto",
                gap: 8,
                marginBottom: 8,
                alignItems: "center",
              }}
            >
              <input
                type="text"
                value={f.name}
                onChange={(e) =>
                  setFoundations((prev) =>
                    prev.map((x, idx) =>
                      idx === i ? { ...x, name: e.target.value } : x
                    )
                  )
                }
                placeholder="Foundation name"
                style={inputStyle}
              />
              <input
                type="text"
                value={f.role}
                onChange={(e) =>
                  setFoundations((prev) =>
                    prev.map((x, idx) =>
                      idx === i ? { ...x, role: e.target.value } : x
                    )
                  )
                }
                placeholder="Role (trustee, officer)"
                style={inputStyle}
              />
              <button
                onClick={() => handleRemoveFoundation(i)}
                style={{
                  background: "transparent",
                  border: `1px solid ${palette.cardBorder}`,
                  borderRadius: 6,
                  padding: 6,
                  cursor: "pointer",
                  color: palette.text3,
                  display: "flex",
                  alignItems: "center",
                }}
                title="Remove row"
              >
                <Trash2 size={ICON_SIZES.xs} />
              </button>
            </div>
          ))}
          <button
            onClick={handleAddFoundation}
            style={{
              background: "transparent",
              border: `1px dashed ${palette.cardBorder}`,
              borderRadius: 6,
              padding: "6px 10px",
              cursor: "pointer",
              color: palette.accent1,
              fontSize: FONT_SIZES.xs,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontFamily: FONT_FAMILY,
            }}
          >
            <Plus size={ICON_SIZES.xs} /> Add foundation
          </button>
        </div>

        {/* Business affiliations */}
        <div>
          <label style={labelStyle}>
            Business / board affiliations
            <InfoBubble
              size={ICON_SIZES.xs}
              text="Executive roles, corporate board seats, or privately-held business ownership. Check LinkedIn public profile and OpenCorporates."
            />
          </label>
          {businesses.map((b, i) => (
            <div
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 1fr auto",
                gap: 8,
                marginBottom: 8,
                alignItems: "center",
              }}
            >
              <input
                type="text"
                value={b.company}
                onChange={(e) =>
                  setBusinesses((prev) =>
                    prev.map((x, idx) =>
                      idx === i ? { ...x, company: e.target.value } : x
                    )
                  )
                }
                placeholder="Company or board"
                style={inputStyle}
              />
              <input
                type="text"
                value={b.role}
                onChange={(e) =>
                  setBusinesses((prev) =>
                    prev.map((x, idx) =>
                      idx === i ? { ...x, role: e.target.value } : x
                    )
                  )
                }
                placeholder="Role (CEO, director)"
                style={inputStyle}
              />
              <button
                onClick={() => handleRemoveBusiness(i)}
                style={{
                  background: "transparent",
                  border: `1px solid ${palette.cardBorder}`,
                  borderRadius: 6,
                  padding: 6,
                  cursor: "pointer",
                  color: palette.text3,
                  display: "flex",
                  alignItems: "center",
                }}
                title="Remove row"
              >
                <Trash2 size={ICON_SIZES.xs} />
              </button>
            </div>
          ))}
          <button
            onClick={handleAddBusiness}
            style={{
              background: "transparent",
              border: `1px dashed ${palette.cardBorder}`,
              borderRadius: 6,
              padding: "6px 10px",
              cursor: "pointer",
              color: palette.accent1,
              fontSize: FONT_SIZES.xs,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontFamily: FONT_FAMILY,
            }}
          >
            <Plus size={ICON_SIZES.xs} /> Add business
          </button>
        </div>

        {/* Researcher notes */}
        <div>
          <label style={labelStyle}>Research notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            placeholder="Anything else of note — sources you checked, dates, caveats about the numbers."
            style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }}
          />
        </div>

        {/* Sources cheat-sheet */}
        <div
          style={{
            padding: 12,
            borderRadius: 7,
            background: palette.cardBg,
            border: `1px solid ${palette.cardBorder}`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              marginBottom: 8,
              fontSize: 11,
              fontWeight: 700,
              color: palette.text3,
              letterSpacing: 0.4,
            }}
          >
            <ExternalLink size={ICON_SIZES.xs} />
            WHERE TO LOOK (FREE PUBLIC SOURCES — LINKS OPEN IN A NEW TAB)
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.6 }}>
            {PUBLIC_SOURCES.map((s, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <div
                  style={{
                    fontWeight: 700,
                    color: palette.text1,
                    fontSize: 11,
                    marginBottom: 2,
                  }}
                >
                  {s.label}
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                  }}
                >
                  {s.links.map((link, j) => (
                    <a
                      key={j}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        padding: "4px 9px",
                        borderRadius: 6,
                        background: `${palette.accent1}10`,
                        color: palette.accent1,
                        textDecoration: "none",
                        fontSize: 11,
                        fontWeight: 600,
                        fontFamily: FONT_FAMILY,
                        border: `1px solid ${palette.accent1}30`,
                        whiteSpace: "nowrap",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `${palette.accent1}20`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = `${palette.accent1}10`;
                      }}
                      title={link.hint}
                    >
                      <ExternalLink size={ICON_SIZES.xs} />
                      {link.name}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            marginTop: 4,
          }}
        >
          <ActionButton
            label="Cancel"
            variant="secondary"
            icon={X}
            onClick={onClose}
            disabled={saving}
          />
          <ActionButton
            label={saving ? "Saving..." : "Save research"}
            icon={Save}
            onClick={handleSave}
            disabled={saving}
          />
        </div>
      </div>
    </Modal>
  );
};
